import { helpers } from "@vuelidate/validators";
import definedRules from "./rules";

// test: jest src/utils/validation/__tests__/index.spec.js

/**
 * Format object-format rules definition for Vuelidate
 *
 * @param {string[]} selectedRuleNames - array of ruleName of definedRules
 * @param {FieldDataModel[]} fields - Fields to validate
 * @returns {Object} - object-format rules definition for Vuelidate
 */
export function generateValidationRules(selectedRuleNames, fields) {
  return fields
    .filter(field => !!field.identifier)
    .reduce((rules, field) => {
      return { ...rules, [field.identifier]: rulesOfField(field, selectedRuleNames) }
    }, {})
}

/**
 * Headlessly validate a single field without any context of Vue components
 *
 * @param {string[]} selectedRuleNames - array of ruleName of definedRules
 * @param {FieldDataModel} field - Field to validate
 * @returns {Object} - { pass: boolean, errors: string[] }
 */
export function validateOneField(selectedRuleNames, field) {
  return validateDataOfField(field, selectedRuleNames)
}

/**
 * Headlessly validate multiple fields without any context of Vue components
 *
 * @param {string[]} selectedRuleNames - array of ruleName of definedRules
 * @param {FieldDataModel[]} fields - Fields to validate
 * @returns {Object} - { [fieldIdentifier: string]: { pass: boolean, errors: string[] } }
 */
export function validateFields(selectedRuleNames, fields) {
  return fields
    .filter(field => !!field.identifier)
    .reduce((rules, field) => {
      return { ...rules, [field.identifier]: validateDataOfField(field, selectedRuleNames) }
    }, {})
}

/**
 * Abstract data model for a field
 *
 * @param {string} identifier - developer should do formatting outside of this unit
 * @param {Object} rawData - raw configuration of the field
 * @param {string} value - required when calling headless validation methods
 */
export class FieldDataModel {
  constructor(identifier, rawData, value) {
    this.identifier = identifier;
    this.rawData = rawData;
    this.value = value;
  }
}

function generateVuelidateValidator(rule, field) {
  return helpers.withMessage(
    rule.errorMessage(field),
    (value, vm) => rule.validator(value, field), // eslint-disable-line no-unused-vars
  )
}

function rulesOfField(field, selectedRuleNames) {
  return definedRules
    .filter(rule => selectedRuleNames.includes(rule.ruleName) && rule.enabled(field))
    .reduce((enabledRules, currentRule) => {
      return { ...enabledRules, [currentRule.ruleName]: generateVuelidateValidator(currentRule, field) }
    }, {})
}

function validateDataOfField(field, selectedRuleNames) {
  const enabledRules = definedRules
    .filter(rule => selectedRuleNames.includes(rule.ruleName) && rule.enabled(field))

  return {
    pass: enabledRules.every(rule => true === rule.validator(field.value, field)),
    errors: enabledRules
      .filter(rule => false === rule.validator(field.value, field))
      .map(rule => rule.errorMessage(field))
  }
}
